import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ICarDetails, ILeasingUsedCarIntegratedValuesRequest } from '@impact/data';
import { combineLatest, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { arrowRightIcon } from '../../../../../../icons';
import { CheckoutFlowService } from '../../../../../../lib/checkout/checkout-flow.service';
import { IMinMaxRanges, ITickmark } from '../../../../../../lib/form-elements/range/range.component';
import {
    LEASING_AGREEMENT_TYPES,
    LEASING_PLAN_TYPES,
    LeasingService,
} from '../../../../../../lib/leasing/leasing.service';
import { UsedCarService } from '../../../../../../lib/used-car-details/used-car.service';
import { CheckoutService } from '../../../../../checkout/checkout.service';
import { IRadioGroupItem } from '../../../../../form-elements/radio-toggle/radio-toggle.component';
import { SettingsService, SiteIds } from '../../../../../../lib/core/settings.service';
import { mergeObjects } from '../../../../../../lib/core/helpers';

@Component({
    selector: 'app-checkout-step-payment-leasing',
    template: `
        <ng-container *ngIf="allowLeasing">
            <section class="tab-group__content-wrap1per flow-step__section-block" *ngIf="checkoutService.getCheckoutData() | async as cd">
                
                <header class="flow-step__section-block-header">
                    <h5>{{ 'checkout.payment_step____leasing____headline' | translate }}</h5>
                    <p>
                        {{ 'checkout.payment_step____leasing____description' | translate }}
                    </p>
                </header>

                <ul class="flow-step__summary">
                    <li class="flow-step__summary-row">
                        <span>
                            <span class="flow-step__summary-row-content bold">
                                {{ cd.product?.make }} {{ cd.product?.model }}
                            </span>
                            <span class="flow-step__summary-row-content text-small text-secondary">
                                {{ cd.product?.variant }}
                            </span>
                        </span>
                        <span class="flow-step__summary-row-content">
                            {{ cd.product?.grossPrice | defaultcurrency }}
                        </span>
                    </li>
                    <li class="flow-step__summary-row ">
                        <span>
                            <span class="flow-step__summary-row-content bold">
                                {{ 'generic.exchange_car' | translate }}
                            </span>
                            <span class="flow-step__summary-row-content text-small text-secondary" *ngIf="cd?.exchangeCar">
                                {{ cd.exchangeCar?.make }} {{ cd.exchangeCar?.model }}
                            </span>
                        </span>
                        <span class="flow-step__summary-row-content" [ngSwitch]="cd?.exchangeCar !== null">
                            <ng-container *ngSwitchCase="true">-{{ cd.exchangeCar?.price | defaultcurrency }}</ng-container>
                            <ng-container *ngSwitchCase="false">{{ 'loan.no_exchange_car_choosen' | translate }}</ng-container>
                        </span>
                    </li>
                    <li class="flow-step__summary-row last-item">
                        <span>
                            <span class="flow-step__summary-row-content bold">
                                <!-- [Leasing ydelse/md.] -->
                                {{ leasingDataTexts?.general?.monthlyPaymentText }}
                            </span>
                        </span>
                        <span class="flow-step__summary-row-content">
                            <app-dynamic-value [isLoading]="leasingConfigDataLoading" [ngSwitch]="leasingConfiguratorData?.monthlyPayment < 1000">
                                <span class="bold" *ngSwitchCase="true">{{'generic.call_for_price' | translate }}</span>
                                <ng-container *ngSwitchCase="false">{{ leasingConfiguratorData?.monthlyPayment | defaultcurrency }}</ng-container>
                            </app-dynamic-value>
                        </span>
                        <!-- <span class="flow-step__summary-row-content" [ngSwitch]="cd?.exchangeCar !== null">
                            <ng-container *ngSwitchCase="true">-{{ cd.exchangeCar?.price | defaultcurrency }}</ng-container>
                            <ng-container *ngSwitchCase="false">{{ leasingConfiguratorData?.downPayment | defaultcurrency }}</ng-container>
                        </span> -->
                    </li>
                </ul>
            </section>

            <!-- LEASING CONFIGURATOR -->
            <section class="flow-step__section-block" *ngIf="checkoutService.getCheckoutData() | async as cd">
                <header class="flow-step__section-block-header">
                    <h5>{{'checkout.payment_step____leasing____configurator_headline' | translate}}</h5>
                    <p>
                        {{ 'checkout.payment_step____leasing____configurator_description' | translate }}
                    </p>
                </header>                

                <app-leasing-configurator class="animate-fade-in" *ngIf="leasingConfiguratorData && leasingConfiguratorRangesData"
                    [downPaymentRange]="leasingConfiguratorRangesData.downPaymentRange"
                    [leasingMonthsRange]="leasingConfiguratorRangesData.leasingMonthsRange"
                    [yearlyDistanceInKmRange]="leasingConfiguratorRangesData.yearlyDistanceInKmRange"

                    [downPayment]="leasingConfiguratorData.downPayment"
                    [monthlyTerms]="leasingConfiguratorData.monthlyTerms"
                    [yearlyDistanceInKm]="leasingConfiguratorData.yearlyDistanceInKm"
                    [leasingTypes]="leasingTypes"
                    [leasingPurposes]="leasingPurposes"

                    [isDisabled]="leasingConfigDataLoading || !!(!leasingPriceIsValid)"

                    [showResidualValue]="false"
                    [leasingTexts]="leasingDataTexts"
                    (handleChange)="updateConfiguratorInputData($event)">
                </app-leasing-configurator>

                <div class="used-car-financing__details-summary">
                    <div class="used-car-financing-loan-details__title">{{ 'generic.details_text' | translate }}</div>

                    <ul class="used-car-financing__details-list">
                        <li role="row" class="used-car-financing__details-list-row">
                            <div role="cell" class="used-car-financing__details-list-cell">
                                {{leasingDataTexts?.general?.monthlyPaymentText}}
                            </div>
                            <div role="cell" class="used-car-financing__details-list-cell">
                                <app-dynamic-value [isLoading]="leasingConfigDataLoading" [ngSwitch]="leasingConfiguratorData?.monthlyPayment < 1000">
                                    <span class="bold" *ngSwitchCase="true">{{'generic.call_for_price' | translate }}</span>
                                    <ng-container *ngSwitchCase="false">{{ leasingConfiguratorData?.monthlyPayment | defaultcurrency }}</ng-container>
                                </app-dynamic-value>
                            </div>
                        </li>
                        <li role="row" class="used-car-financing__details-list-row">
                            <div role="cell" class="used-car-financing__details-list-cell">
                                {{leasingDataTexts?.leasingPlanStep?.firstTimePaymentText}}
                            </div>

                            <div role="cell" class="used-car-financing__details-list-cell" [ngSwitch]="leasingPriceIsValid">
                                <app-dynamic-value [isLoading]="leasingConfigDataLoading">
                                    <ng-container *ngSwitchCase="true">{{ leasingConfiguratorData?.downPayment | defaultcurrency }}</ng-container>
                                    <ng-container *ngSwitchCase="false">&mdash;</ng-container>
                                </app-dynamic-value>
                            </div>
                        </li>
                        <li role="row" class="used-car-financing__details-list-row">
                            <div role="cell" class="used-car-financing__details-list-cell">
                                {{leasingDataTexts?.leasingPlanStep?.durationOfLoanText}}
                            </div>
                            <div role="cell" class="used-car-financing__details-list-cell" [ngSwitch]="leasingPriceIsValid">
                                <app-dynamic-value [isLoading]="leasingConfigDataLoading">
                                    <ng-container *ngSwitchCase="true">{{ leasingConfiguratorData?.monthlyTerms }} måneder</ng-container>
                                    <ng-container *ngSwitchCase="false">&mdash;</ng-container>
                                </app-dynamic-value>
                            </div>
                        </li>
                        <li role="row" class="used-car-financing__details-list-row">
                            <div role="cell" class="used-car-financing__details-list-cell">
                                {{leasingDataTexts?.general?.kmPerYearText}}
                            </div>
                            <div role="cell" class="used-car-financing__details-list-cell" [ngSwitch]="leasingPriceIsValid">
                                <app-dynamic-value [isLoading]="leasingConfigDataLoading">
                                    <ng-container *ngSwitchCase="true">{{ leasingConfiguratorData?.yearlyDistanceInKm | number: '1.0-0' }}</ng-container>
                                    <ng-container *ngSwitchCase="false">&mdash;</ng-container>
                                </app-dynamic-value>
                            </div>
                        </li>
                    </ul>
                </div>
            </section>

            <div class="center grid-spacing-normal">
                <app-button
                    *ngIf="!(checkoutFlowService.atFlowStart$ | async)"
                    buttonClass="button--tertiary button--has-spacing button--has-bottom-spacing"
                    [title]="'checkout.button____go_back_to_previous_step' | translate"
                    (handleClick)="checkoutFlowService.prevStep()"
                ></app-button>
                <app-button
                    buttonClass="button--primary button--has-spacing button--has-bottom-spacing"
                    [title]="'checkout.button____choose_and_proceed' | translate"
                    (handleClick)="handleSubmit()"
                    [disabled]="leasingConfigDataLoading || !leasingPriceIsValid"
                    [loading]="!!(checkoutService.isCheckoutLoading$ | async)">
                    <i aria-hidden="true" class="button__icon">${arrowRightIcon}</i>
                </app-button>
            </div>
        </ng-container>
        
        <section class="tab-group__content-wrap1per flow-step__section-block" *ngIf="!allowLeasing">
            <header class="flow-step__section-block-header">
                <h5 class="center">Leasing ikke tilgængelig</h5>
            </header>
        </section>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutStepPaymentLeasingComponent implements OnDestroy {
    @Input() leasingCheckoutData?: any;
    @Input() allowLeasing: boolean;
    @Input() leasingTypes?: IRadioGroupItem[] | null ;
    @Input() leasingPurposes?: IRadioGroupItem[] | null;

    activeLeasingAgreement: LEASING_AGREEMENT_TYPES;
    leasingAgreementTypes = LEASING_AGREEMENT_TYPES;

    // Leasing ranges
    leasingMonthsRange: ITickmark[] = [];
    firstPaymentRange: IMinMaxRanges;
    yearlyDistanceInKmRange: IMinMaxRanges;
    residualValueRange: IMinMaxRanges;

    leasingConfiguratorData: any;
    leasingConfiguratorRangesData: any;
    leasingTypeForm: UntypedFormGroup;

    carData: ICarDetails;
    leasingDataTexts: any;
    leasingConfigDataLoading: boolean;
    leasingConfigDataReady: boolean;
    leasingPriceIsValid: boolean;
    siteId: string;
    private staticMotorpointRequestData: ILeasingUsedCarIntegratedValuesRequest;

    private unsubscribe = new Subject<void>();

    constructor(
        private usedCarService: UsedCarService,
        public checkoutService: CheckoutService,
        public checkoutFlowService: CheckoutFlowService,
        private settingsService: SettingsService,
        private leasingService: LeasingService,
        private cdr: ChangeDetectorRef,
        private readonly fb: UntypedFormBuilder,
    ) {
        this.leasingConfigDataReady = false;

        this.settingsService.getSiteId().pipe(takeUntil(this.unsubscribe)).subscribe((siteId) => {
            this.siteId = siteId;
        });

        const carData$ = this.usedCarService.carDetails$;
        const leasingCheckoutData$ = this.checkoutService.getCheckoutData();
        const leasingGlobalData$ = this.leasingService.getLeasingGlobalData();
        const leasingConfiguratorData$ = this.checkoutService.leasingConfigDataAndRanges$;

        const connectStream$ = combineLatest([carData$, leasingCheckoutData$, leasingGlobalData$, leasingConfiguratorData$]);

        connectStream$.pipe(takeUntil(this.unsubscribe)).subscribe(([carData, leasingCheckoutData, leasingGlobalData, leasingConfiguratorData]) => {
            this.carData = carData;
            this.leasingDataTexts = leasingGlobalData.texts;

            this.staticMotorpointRequestData = {
                carMilage: this.carData?.mileage?.data,
                downpayment: undefined,
                durationInMonths: undefined,
                firstRegistrationDate: String(this.carData.registrationDate?.data),
                fueltype: this.carData?.propellant?.data === 'Benzin' ? 'B' : this.carData?.propellant?.data === 'El' ? 'E' : 'D',
                km: undefined,
                isActiveMotorPoint: true,
                make: this.carData?.make?.value,
                model: this.carData?.model?.value,
                modelDescription: this.carData?.variant?.value,
                registrationFee: this.carData?.registrationFee?.data,
                steelPrice: this.carData?.steelPrice?.data,
                vehicleType: 'P',
            };

            // Check if existing leasing data
            if (leasingCheckoutData?.lease) {
                this.leasingConfiguratorData = {
                    downPayment: leasingCheckoutData.lease?.downPayment,
                    monthlyPayment: leasingCheckoutData.lease?.monthlyPayment,
                    monthlyTerms: leasingCheckoutData.lease?.duration,
                    residualValue: leasingCheckoutData.lease?.residualValue,
                    selectedLeasingPlanType: leasingCheckoutData.lease?.leasingType,
                    totalPrice: leasingCheckoutData.lease?.totalPrice,
                    yearlyDistanceInKm: leasingCheckoutData.lease?.kmPerYear,
                };
            } else {
                this.leasingConfiguratorData = {
                    totalPrice: 0,
                    selectedLeasingPlanType: leasingConfiguratorData.leasingType,
                    ...leasingConfiguratorData?.leasingData,
                };
            }

            this.leasingConfiguratorRangesData = {
                ...leasingConfiguratorData?.leasingRanges,
            };

            
            this.leasingPriceIsValid = this.leasingConfiguratorData?.monthlyPayment >= 1000 ? true : false;

            this.leasingConfigDataReady = true;
            this.cdr.markForCheck();
        });

        if (this.siteId !== SiteIds.MOTORPOINT) {
            this.leasingTypeForm = this.fb.group({
                leasingPlanType: [LEASING_AGREEMENT_TYPES.OPERATIONAL, Validators.required]
            });
        }
    }

    getUsedCarPricePerMonth(): Observable<any> {
        return this.leasingService.getUsedCarLeasingPrice(this.carData, this.leasingConfiguratorData);
    }

    validateLeasingToggles(value: string): boolean {
        if (value === undefined) {
            // If value is undefined, we return true, that is the default for fetching Motorpoint leasing data
            return true;
        }

        if (value === LEASING_PLAN_TYPES.PURPOSE_BUSINESS || value === LEASING_PLAN_TYPES.TYPE_FINANCIAL) {
            return true;
        }

        return false;
    }

    updateConfiguratorInputData(data: any) {
        this.leasingConfigDataLoading = true;
        this.cdr.detectChanges();

        if (isNaN(data.residualValue)) {
            data.residualValue = 0;
        }

        this.leasingConfiguratorData.downPayment = data.downPayment;
        this.leasingConfiguratorData.monthlyTerms = data.monthlyTerms;
        this.leasingConfiguratorData.yearlyDistanceInKm = data.yearlyDistanceInKm;
        this.leasingConfiguratorData.residualValue = data.residualValue;
        this.leasingConfiguratorData.leasingType = data.leasingPlanType;
        this.leasingConfiguratorData.leasingtypeForPriceCalculation = data.leasingPurpose;

        if (this.siteId === SiteIds.MOTORPOINT) {
            const dynamicData = {
                downPayment: this.leasingConfiguratorData?.downPayment,
                durationInMonths: this.leasingConfiguratorData?.monthlyTerms ?? 12,
                isLeasingTypeFinancial: this.validateLeasingToggles(this.leasingConfiguratorData.leasingType),
                isLeasingForBusiness: this.validateLeasingToggles(this.leasingConfiguratorData.leasingtypeForPriceCalculation),
                km: this.leasingConfiguratorData?.yearlyDistanceInKm
            };
    
            const reqBody = mergeObjects(this.staticMotorpointRequestData, dynamicData);

            this.leasingService.getUsedCarLeasingIntegratedValues(reqBody).subscribe((data: any) => {
                this.leasingPriceIsValid = data.pricePerMonth >= 1000;

                this.leasingService.broadcastUsedCarLeasingPrice(data.pricePerMonth);

                // Only update the data if the price is valid, to show correct state in slider when not
                if (this.leasingPriceIsValid) {
                    // Update leasing ranges data
                    this.leasingConfiguratorRangesData.downPaymentRange = {
                        minValue: data.downpaymentMin,
                        maxValue: data.downpaymentMax
                    };
        
                    // Create Months range
                    this.leasingConfiguratorRangesData.leasingMonthsRange = [];
                    for (let i = 0; i < data.leasingDurationValues.length; i++) {
                        this.leasingConfiguratorRangesData.leasingMonthsRange.push({
                            label: String(data.leasingDurationValues[i]),
                            isDefault: data.leasingDurationValues[i] === data.defaultDuration,
                            value: data.leasingDurationValues[i],
                        });
                    };
        
                    this.leasingConfiguratorRangesData.yearlyDistanceInKmRange = {
                        minValue: data.kmPerYearMin,
                        maxValue: data.kmPerYearMax
                    };

                    // Update Leasing slider data
                    this.leasingConfiguratorData.downPayment = data.defaultDownpayment;
                    this.leasingConfiguratorData.monthlyTerms = data.defaultDuration;
                    this.leasingConfiguratorData.yearlyDistanceInKm = data.defaultKmPerYear;
                    this.leasingConfiguratorData.monthlyPayment = data.pricePerMonth;
                }

                this.leasingConfigDataLoading = false;
                this.leasingConfigDataReady = true;
                this.cdr.markForCheck();
            },
            (error: any) => {
                console.warn('[BN Error fetching data]: Probably incorrect data');
                console.error('[BN Error fetching data]', error);
                this.leasingPriceIsValid = false;
                this.leasingConfigDataLoading = false;
                this.leasingConfigDataReady = true;
                this.cdr.markForCheck();
            });
        } else {
            // Get leasing price
            this.getUsedCarPricePerMonth().subscribe((data: any) => {
                this.leasingConfiguratorData.monthlyPayment = data?.pricePerMonth;
                this.leasingConfigDataLoading = false;
                this.leasingConfigDataReady = true;
                this.leasingPriceIsValid = this.leasingConfiguratorData.monthlyPayment >= 1000 ? true : false;
                this.cdr.markForCheck();
            });
        }

    }

    updateLeasingData(data?: any) {
        if (!data) {
            return;
        }

        this.leasingConfigDataLoading = true;

        this.leasingConfiguratorData = {
            ...this.leasingConfiguratorData,
            ...data,
        };

        this.cdr.markForCheck();

        setTimeout(() => {
            this.leasingConfigDataLoading = false;
            this.cdr.markForCheck();
        }, 1000);
    }

    handleSubmit() {
        this.checkoutService.getCheckoutData().pipe(takeUntil(this.unsubscribe)).subscribe((checkoutData: any) => {
            if (checkoutData.purchaseOrderId) {
                this.unsubscribe.next();
                if (!this.leasingConfiguratorData.selectedLeasingPlanType) {
                    this.leasingConfiguratorData.selectedLeasingPlanType = this.leasingConfiguratorData.leasingType;
                }
                this.checkoutService.setLeasingPayment(checkoutData.purchaseOrderId, this.leasingConfiguratorData);
            }
        });
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
